import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import actions from 'redux/actions'
import { closeModal } from 'sb-modal'
import { useMemo, useEffect, useCallback, useScriptsLoader } from 'sb-hooks'
import { links, constants, object } from 'common/helpers'
import { callAnalytics } from 'redux/models/payment/util'

import config from 'app-config'

import { Button } from 'components/_new/controls'

import s from './PayPalPaymentButton.scss'


const clientId = config.runEnv === 'prod'
  ? 'AcACdB2RGxb-23peUt2ovwfDUXBYSGbBlv2iEsMOV94YHcKqj4ATAGnWYsbddFAGWR1HtkbGo5ASw5ao'
  : 'AZDxjDScFpQtjWTOUtWKbyN_bDt4OgqaF4eYXlewfBP4-8aqX3PiV8e1GWU6liB2CUXlkA59kJXE7M6R'

const scriptUrl = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD&intent=authorize&disable-card=visa,mastercard,amex`

let isErrorHandlingAllowed = true

let isBeginActionComplete = true

const closeAddressVerificationModal = () => {
  actions.models.modals.close(constants.modals.AddressValidation)
  closeModal('addressValidation')
}

const createPaymentProcessHandler = ({ payPalBeginAction, onClick, handleError }) => () => (
  // TODO that onClick returns value isn't good at all
  // Onclick return data from Address validation modal
  onClick()
    .then((data) => payPalBeginAction(data))
    .then(
      (payPalToken) => {
        isBeginActionComplete = true

        return payPalToken
      },
      (error) => {
        // catch err and don't return Promise bcs paypal will send 2nd begin request (in case of error only)
        // TODO check if paypal bug with twice request firing is resolved and remove this onReject callback with 'isErrorHandlingAllowed' hack
        handleError(error)
        isErrorHandlingAllowed = false
      }
    )
)

const createAuthorizeHandler = ({ paymentType, payPalCommitAction, onSuccess }) => () => {
  if (isBeginActionComplete) {
    isBeginActionComplete = false

    closeAddressVerificationModal()
    actions.models.ui.showRequestLoader()

    return payPalCommitAction({ paymentType })
      .then((data) => {
        if (paymentType !== constants.paymentTypes.updateInfo) {
          callAnalytics()
          actions.models.router.push(links.thankYou)
          return
        }

        if (typeof onSuccess === 'function') {
          onSuccess(data)
        }
      })
      .finally(() => {
        actions.models.ui.hideRequestLoader()
      })
  }

  return Promise.reject()
}

// error handling may be restricted from handlePaymentProcess if begin request is failed
// so we need to reset it
const onPayPalButtonClick = () => {
  isErrorHandlingAllowed = true
}

const usePayPalButtonRender = ({ isLoaded, handleAuthorize, handlePaymentProcess, handleError, handleCancel }) =>
  useEffect(() => {
    const paypal = object.get(window, 'paypal')

    if (paypal && paypal.Buttons && isLoaded) {
      paypal.Buttons({
        createOrder:handlePaymentProcess,
        onApprove: handleAuthorize,
        onError: handleError,
        onClick: onPayPalButtonClick,
      })
        .render('#paypalBtn')
    }
  }, [ isLoaded, handleAuthorize, handlePaymentProcess, handleError, handleCancel ])

const PayPalPaymentButtonNew = (props) => {
  const {
    className, children, title, disabled, fullWidth, fullWidthOnMobile, color = 'brand',
    paymentType, payPalBeginAction, payPalCommitAction,
    onSuccess, onError, onCancel, onClick,
    dataTestId,
  } = props

  const { isLoaded } = useScriptsLoader([ scriptUrl ])

  const handleError = useCallback((error = {}) => {
    if (!isErrorHandlingAllowed) {
      return
    }

    const { title, message, statusCode } = error
    let content

    // show our backend messages only, not paypal
    if (statusCode === 409) {
      content = message
    }

    closeAddressVerificationModal()
    actions.models.modals.open(constants.modals.ErrorPayPal, { type: 'error', title, content })

    if (typeof onError === 'function') {
      onError(error)
    }
  }, [ onError ])

  const handleCancel = useCallback(() => {
    closeAddressVerificationModal()

    if (typeof onCancel === 'function') {
      onCancel()
    }
  }, [ onCancel ])

  const handlePaymentProcess = useMemo(() => (
    createPaymentProcessHandler({ payPalBeginAction, handleError, onClick })
  ), [ payPalBeginAction, handleError, onClick ])

  const handleAuthorize = useMemo(() => (
    createAuthorizeHandler({ paymentType, payPalCommitAction, handleError, onSuccess })
  ), [ paymentType, payPalCommitAction, handleError, onSuccess ])

  usePayPalButtonRender({ isLoaded, handleAuthorize, handlePaymentProcess, handleError, handleCancel })

  return (
    <Button
      className={cx(s.paypalButton, className)}
      tag="div"
      color={color}
      size="md"
      weight="normal"
      loading={!isLoaded}
      disabled={disabled}
      fullWidth={fullWidth}
      fullWidthOnMobile={fullWidthOnMobile}
      dataTestId={dataTestId}
      title={title}
      afterNode={(
        <div id="paypalBtn" />
      )}
    >
      {children}
    </Button>
  )
}

PayPalPaymentButtonNew.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  title: PropTypes.object,
  paymentType: PropTypes.string.isRequired,
  payPalBeginAction: PropTypes.func.isRequired,
  payPalCommitAction: PropTypes.func.isRequired,

  onClick: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  onCancel: PropTypes.func,
}


export default PayPalPaymentButtonNew
